/**
   Copyright (c)2021 3Key Company s.r.o., All Rights Reserved

   It is strictly prohibited to distribute the source code,
   transpiled and compiled code or its parts without previous
   agreement of the 3Key Company board.
*/

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CompanyUserModel } from "data/models/CompanyModel";


export interface UsersState {

   usersLoading: boolean;
   users: CompanyUserModel[];

   selectedUser?: CompanyUserModel;

   userCreating: string[];
   userLoading: string[];
   userUpdating: string[];
   userDeleting: string[];
   userInviting: string[];

}


const initialState: UsersState = {

   usersLoading: false,
   users: [],

   userCreating: [],
   userLoading: [],
   userUpdating: [],
   userDeleting: [],
   userInviting: []

}


export const slice = createSlice({

   name: "users",

   initialState,

   reducers: {

      loadUsers: (state, action: PayloadAction<{ companyId: string }>) => {
         state.usersLoading = true;
      },

      usersLoaded: (state, action: PayloadAction<{ users: CompanyUserModel[] }>) => {
         state.usersLoading = false;
         state.users = action.payload.users;
         state.selectedUser = undefined;
      },

      usersLoadingError: (state, action: PayloadAction<{err: any}>) => {
         state.usersLoading = false;
         console.error(action.payload.err);
      },

      createUser: (state, action: PayloadAction<{ user: CompanyUserModel }>) => {
         state.userCreating.push(action.payload.user.companyId + action.payload.user.username);
      },

      userCreated: (state, action: PayloadAction<{ user: CompanyUserModel }>) => {

         const indexCreating = state.userCreating.indexOf(action.payload.user.companyId + action.payload.user.username);
         state.userCreating.splice(indexCreating);

         state.users.push(action.payload.user);

      },

      userCreatingError: (state, action: PayloadAction<{ user: CompanyUserModel, err: any }>) => {

         const indexCreating = state.userCreating.indexOf(action.payload.user.companyId + action.payload.user.username);
         state.userCreating.splice(indexCreating);

         console.error(action.payload.err);

     },

      loadUser: (state, action: PayloadAction<{ companyId: string, id: string }>) => {
         state.userLoading.push(action.payload.companyId + action.payload.id);
      },

      userLoaded: (state, action: PayloadAction<{ user: CompanyUserModel }>) => {

         const indexLoading = state.userLoading.indexOf(action.payload.user.companyId + action.payload.user.id);
         state.userLoading.splice(indexLoading);

         if (state.selectedUser && state.selectedUser.companyId === action.payload.user.companyId && state.selectedUser.id === action.payload.user.id) state.selectedUser = action.payload.user;

         const index = state.users.findIndex(g => g.companyId === action.payload.user.companyId && g.id === action.payload.user.id);

         if (index) {
            state.users[index] = action.payload.user;
         } else {
            state.users.push(action.payload.user);
         }

      },

      userLoadingError: (state, action: PayloadAction<{ companyId: string, id: string, err: any }>) => {

         const indexLoading = state.userLoading.indexOf(action.payload.companyId + action.payload.id);
         state.userLoading.splice(indexLoading);

         console.error(action.payload.err);

      },


      selectUserById: (state, action: PayloadAction<{ companyId: string, id: string }>) => {
         state.selectedUser = state.users.find(u => u.companyId === action.payload.companyId && u.id === action.payload.id);
      },



      updateUser: (state, action: PayloadAction<{ user: CompanyUserModel }>) => {
         state.userUpdating.push(action.payload.user.companyId + action.payload.user.id);
      },

      userUpdated: (state, action: PayloadAction<{ user: CompanyUserModel }>) => {

         const indexUpdating = state.userUpdating.indexOf(action.payload.user.companyId + action.payload.user.id);
         state.userUpdating.splice(indexUpdating, 1);

         if (state.selectedUser && state.selectedUser.companyId === action.payload.user.companyId && state.selectedUser.id === action.payload.user.id) state.selectedUser = action.payload.user;

         const index = state.users.findIndex(g => g.companyId === action.payload.user.companyId && g.id === action.payload.user.id);

         if (index >=0 ) {
            state.users[index] = action.payload.user
         } else {
            console.error(`Failed to update a users state. User for company ${action.payload.user.companyId} with id ${action.payload.user.id} not found`);
         }

      },

      userUpdateError: (state, action: PayloadAction<{ user: CompanyUserModel, err: any }>) => {

         const indexUpdating = state.userUpdating.indexOf(action.payload.user.companyId + action.payload.user.id);
         state.userUpdating.splice(indexUpdating, 1);


         console.error(action.payload.err);
      },


      deleteUser: (state, action: PayloadAction<{ user: CompanyUserModel }>) => {
         state.userDeleting.push(action.payload.user.companyId + action.payload.user.id);
      },

      userDeleted: (state, action: PayloadAction<{ user: CompanyUserModel }>) => {

         const indexDeleting = state.userDeleting.indexOf(action.payload.user.companyId + action.payload.user.id);
         state.userDeleting.splice(indexDeleting, 1);

         if (state.selectedUser && state.selectedUser.companyId === action.payload.user.companyId && state.selectedUser.id === action.payload.user.id) state.selectedUser = undefined;

         const index = state.users.findIndex(u => u.companyId === action.payload.user.companyId && u.id === action.payload.user.id);

         if (index >=0 ) {
            state.users.splice(index, 1);
         } else {
            console.error(`Failed to delete a user from the user state. User for company ${action.payload.user.companyId} with id ${action.payload.user.id} not found`);
         }

      },

      userDeleteError: (state, action: PayloadAction<{ user: CompanyUserModel, err: any }>) => {

         const indexDeleting = state.userDeleting.indexOf(action.payload.user.companyId + action.payload.user.id);
         state.userDeleting.splice(indexDeleting, 1);

         console.error(action.payload.err);

      },

      inviteUser: (state, action: PayloadAction<{ user: CompanyUserModel }>) => {
         state.userInviting.push(action.payload.user.companyId + action.payload.user.id);
      },

      userInvited: (state, action: PayloadAction<{ user: CompanyUserModel }>) => {

         const indexInviting = state.userInviting.indexOf(action.payload.user.companyId + action.payload.user.id);
         state.userInviting.splice(indexInviting, 1);

         if (state.selectedUser && state.selectedUser.companyId === action.payload.user.companyId && state.selectedUser.id === action.payload.user.id) state.selectedUser = action.payload.user;

         const index = state.users.findIndex(g => g.companyId === action.payload.user.companyId && g.id === action.payload.user.id);

         if (index >=0 ) {
            state.users[index] = action.payload.user
         } else {
            console.error(`Failed to update a users state. User for company ${action.payload.user.companyId} with id ${action.payload.user.id} not found`);
         }

      },

      userInvitationError: (state, action: PayloadAction<{ user: CompanyUserModel, err: any }>) => {

         const indexInviting = state.userInviting.indexOf(action.payload.user.companyId + action.payload.user.id);
         state.userInviting.splice(indexInviting, 1);

         console.error(action.payload.err);

      }


   }

});


export const actions = slice.actions;

export default slice.reducer;



